import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaHome } from "react-icons/fa";
import "./Navbar.css";
import Cookies from "js-cookie";
import { API_BASE } from "../api/config";
import { GeminiBalken } from "./GeminiWarnung";
import { useAuth } from "../context/AuthContext";
import { WELLE38_UI } from "../featureFlags";

function Navbar({ isAuthenticated }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMenu = () => setMobileMenuOpen(false);
  const isNurZeit = user?.zugang_typ === "nur_zeiterfassung";
  const hasModule = (key) => {
    if (isNurZeit) return false;
    if (!user || !user.module || user.module.length === 0) return true;
    return user.module.includes(key);
  };
  const handleLogout = async () => {
    await logout();
    navigate("/");
  };
  const [version, setVersion] = useState("");
  const [showTestModal, setShowTestModal] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [autoBatch, setAutoBatch] = useState({
    enabled: false,
    interval_minutes: 15,
    last_run: "",
    last_status: "idle",
    last_files: 0,
    currently_processing: false,
  });
  const [autoBatchLoading, setAutoBatchLoading] = useState(false);
  const [kiLoading, setKiLoading] = useState(false);
  // Version aus RELEASE.json laden
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/RELEASE.json`)
      .then(r => r.json())
      .then(d => setVersion(d.version || ""))
      .catch(() => {});
  }, []);

  // Theme wird nicht mehr hier erzwungen — es hängt seit Welle 99 am Konto
  // (utils/darstellung.js, Einstellungsseite). Der alte localStorage-Schlüssel
  // "darkMode" spielt keine Rolle mehr.

  const API_BASE_NAV = `${API_BASE}/rename-me-ocr/api`;
  const AUTO_BATCH_BASE = `${API_BASE}/rename-me-ocr/api/auto-batch`;
  const TEST_BASE = `${API_BASE_NAV}/test`;

  // Auto-Batch Status laden
  const fetchAutoBatchConfig = async () => {
    try {
      const res = await fetch(`${AUTO_BATCH_BASE}/config`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setAutoBatch(data);
      }
    } catch (_) {
      // Backend nicht erreichbar → ignorieren
    }
  };

  // Auto-Batch Status beim Mount und alle 30s aktualisieren
  useEffect(() => {
    fetchAutoBatchConfig();
    const id = setInterval(fetchAutoBatchConfig, 30000);
    return () => clearInterval(id);
  }, []);

  const toggleAutoBatch = async () => {
    setAutoBatchLoading(true);
    try {
      const newEnabled = !autoBatch.enabled;
      const res = await fetch(`${AUTO_BATCH_BASE}/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ enabled: newEnabled }),
      });
      if (res.ok) {
        const data = await res.json();
        setAutoBatch((alt) => ({ ...alt, ...data }));   // gemini-Stand vom letzten Abruf behalten
      }
    } catch (err) {
      console.error('Auto-Batch Toggle fehlgeschlagen:', err);
    } finally {
      setAutoBatchLoading(false);
    }
  };

  const toggleKi = async () => {
    setKiLoading(true);
    try {
      const newKiEnabled = !autoBatch.ki_enabled;
      const res = await fetch(`${AUTO_BATCH_BASE}/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ ki_enabled: newKiEnabled }),
      });
      if (res.ok) {
        const data = await res.json();
        setAutoBatch(data);
      }
    } catch (err) {
      console.error('KI Toggle fehlgeschlagen:', err);
    } finally {
      setKiLoading(false);
    }
  };

  async function fetchJSON(url, options = {}) {
    try {
      const sessionId = localStorage.getItem("sessionId") || Cookies.get("sessionId") || "";
      const merged = {
        credentials: 'include',
        mode: 'cors',
        cache: 'no-store',
        ...options,
      };
      const headers = {
        Accept: 'application/json',
        ...(options.headers || {}),
      };
      if (sessionId) headers['session-id'] = sessionId;
      merged.headers = headers;

      const res = await fetch(url, merged);
      if (!res.ok) {
        let extra = "";
        try {
          const t = await res.text();
          if (t) extra = ` — ${t.slice(0, 500)}`;
        } catch (_) {}
        throw new Error(`${res.status} ${res.statusText}${extra}`);
      }
      return await res.json();
    } catch (err) {
      setErrorMsg(`Fehler: ${err.message}`);
      throw err;
    }
  }

  const openTestModal = async () => {
    setShowTestModal(true);
    setErrorMsg(null);
    try {
      const lr = await fetchJSON(`${TEST_BASE}/last`).catch(() => null);
      if (lr) setLastResult(lr?.result || lr);
    } catch (_) { /* ignore */ }
  };

  const closeTestModal = () => {
    setShowTestModal(false);
  };

  const startTest = async () => {
    setErrorMsg(null);
    setProgress(null);
    setLastResult(null);
    try {
      await fetchJSON(`${TEST_BASE}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      setTestRunning(true);
    } catch (err) {
      // errorMsg already set in fetchJSON
    }
  };

  const pollProgress = async () => {
    try {
      const p = await fetchJSON(`${TEST_BASE}/progress`);
      setProgress(p?.progress || p);
      const finished = Boolean(p && (p.finished || p.done || p.status === "finished" || p.state === "finished" || p.running === false));
      if (finished) {
        setTestRunning(false);
        try {
          const lr = await fetchJSON(`${TEST_BASE}/last`);
          setLastResult(lr);
        } catch (_) { /* ignore */ }
      }
    } catch (_) {
      // swallow during polling
    }
  };

  useEffect(() => {
    let id;
    if (testRunning) {
      id = setInterval(pollProgress, 1500);
      // initial
      pollProgress();
    }
    return () => {
      if (id) clearInterval(id);
    };
  }, [testRunning]);

  const overlayStyle = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 };
  const modalStyle = { background: 'var(--bg-card)', borderRadius: 'var(--radius-2xl)', padding: 'var(--space-6)', width: 'min(900px, 95vw)', maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow-2xl)', animation: 'scaleIn 0.2s ease-out' };
  const footerStyle = { display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-6)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-200)' };
  const badge = (txt, color) => (<span style={{ padding: '4px 12px', borderRadius: 'var(--radius-full)', background: color, color: '#fff', fontSize: 'var(--text-xs)', fontWeight: 600 }}>{txt}</span>);

  return (
    <>
      <nav className={`navbar ${mobileMenuOpen ? "navbar-menu-open" : ""}`}>
        <div className="navbar-brand" onClick={() => navigate(isNurZeit ? "/arbeitsnachweis" : "/")} title="Home">
          <div className="navbar-icon"><FaHome /></div>
          <span className="navbar-title">Sijelly Toolbox</span>
          {version && <span className="navbar-version">v{version}</span>}
        </div>
        <button
          className={`navbar-burger ${mobileMenuOpen ? "open" : ""}`}
          onClick={() => setMobileMenuOpen(v => !v)}
          aria-label="Menü"
          aria-expanded={mobileMenuOpen}
        >
          <span></span><span></span><span></span>
        </button>
        {mobileMenuOpen && <div className="navbar-backdrop" onClick={closeMenu} />}
        <div className={`links ${mobileMenuOpen ? "open" : ""}`} onClick={(e) => {
          // Auf Klick eines Links/Buttons das Menü schließen — außer es ist ein Toggle
          if (e.target.closest("a") || e.target.closest(".navbar-logout-btn")) closeMenu();
        }}>
          {!isAuthenticated && mobileMenuOpen && (
            <div className="navbar-drawer-hint">
              Du bist nicht angemeldet. Module sind erst nach Login verfügbar.
            </div>
          )}
          {isNurZeit && (
            <Link to="/arbeitsnachweis" className="active">⏱ Zeiterfassung</Link>
          )}
          {!isNurZeit && <Link to="/" className="active">Home</Link>}
          {hasModule("zaehlerstand") && <Link to="/zaehlerstand" className={isAuthenticated ? "active" : "disabled-link"}
            title="Zählerstand VBA Sync">
            VBA
          </Link>}
          {hasModule("datenbank.datenansicht") && <Link to="/data" className={isAuthenticated ? "active" : "disabled-link"}>
            Datenansicht
          </Link>}
          {hasModule("datenbank.db_tabellen") && <Link to="/database-tables" className={isAuthenticated ? "active" : "disabled-link"}>
             DB-Tabellen
          </Link>}
            {hasModule("datenbank2") && <Link to="/database-tables-2" className={isAuthenticated ? "active" : "disabled-link"}>
             DB-Tabellen 2
          </Link>}
          {hasModule("datenbank.strg") && <Link to="/strg" className={isAuthenticated ? "active" : "disabled-link"}>
            STRG-Tabelle
          </Link>}
          {hasModule("monitoring") && <Link to="/monitoring" className={isAuthenticated ? "active" : "disabled-link"}>
            Monitoring
          </Link>}
          {hasModule("rename.batch") && <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              if (isAuthenticated) {
                openTestModal();
              }
            }}
            className={isAuthenticated ? "active" : "disabled-link"}
            aria-disabled={!isAuthenticated}
            title={isAuthenticated ? "Testlauf öffnen" : "Bitte zuerst einloggen"}
            role="button"
          >
            Test
          </Link>}
          
          {/* Auto-Batch Toggle */}
          {hasModule("rename.batch") && <button
            className={`auto-batch-toggle ${autoBatch.enabled ? 'active' : ''} ${autoBatch.currently_processing ? 'processing' : ''}`}
            style={autoBatch.gemini?.gesperrt ? { borderColor: 'var(--color-error)', color: 'var(--color-error)' } : undefined}
            onClick={isAuthenticated ? toggleAutoBatch : undefined}
            disabled={autoBatchLoading || !isAuthenticated}
            title={
              !isAuthenticated
                ? 'Bitte zuerst einloggen'
                : autoBatch.gemini?.gesperrt
                ? `${autoBatch.gemini.text} — der Batch läuft nicht, bis Gemini wieder geht`
                : autoBatch.enabled
                ? `Auto-Batch AN (alle ${autoBatch.interval_minutes} Min)${autoBatch.last_run ? ` · Letzter Lauf: ${autoBatch.last_run}` : ''}`
                : 'Auto-Batch AUS – Klicken zum Aktivieren'
            }
            aria-label="Auto-Batch Toggle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            <span className="auto-batch-label">
              {autoBatch.gemini?.gesperrt ? 'Auto ⚠' : autoBatch.currently_processing ? 'Läuft…' : 'Auto'}
            </span>
            {autoBatch.enabled && <span className="auto-batch-dot" />}
          </button>}

          {/* KI Toggle */}
          {hasModule("ki.lernen") && <button
            className={`ki-toggle ${autoBatch.ki_enabled ? 'active' : ''}`}
            onClick={isAuthenticated ? toggleKi : undefined}
            disabled={kiLoading || !isAuthenticated}
            title={
              !isAuthenticated
                ? 'Bitte zuerst einloggen'
                : autoBatch.ki_enabled
                ? 'KI ist AN – Klicken zum Deaktivieren'
                : 'KI ist AUS – Klicken zum Aktivieren'
            }
            aria-label="KI Toggle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a4 4 0 0 1 4 4v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V6a4 4 0 0 1 4-4z"/>
              <path d="M9 8v1a3 3 0 0 0 6 0V8"/>
              <path d="M12 12v3"/>
              <path d="M8 18h8"/>
              <path d="M10 18v3"/>
              <path d="M14 18v3"/>
              <circle cx="9" cy="5.5" r="0.5" fill="currentColor"/>
              <circle cx="15" cy="5.5" r="0.5" fill="currentColor"/>
            </svg>
            <span className="ki-toggle-label">KI</span>
            {autoBatch.ki_enabled && <span className="ki-dot" />}
          </button>}

          {/* Env Badge */}
          {autoBatch.env && (
            <span className={`env-badge env-${autoBatch.env}`}>
              {autoBatch.env.toUpperCase()}
            </span>
          )}

          {/* Dark-Mode-Toggle entfernt — es gibt nur noch Darkmode */}

          {/* User-Badge + Admin-Link + Logout */}
          {user && (
            <div className="navbar-user-group">
              <span
                className={`navbar-user-badge ${user.rolle === 'admin' ? 'admin' : ''}`}
                style={{ cursor: WELLE38_UI ? "pointer" : "default" }}
                title={WELLE38_UI ? "Meine Einstellungen" : undefined}
                onClick={WELLE38_UI ? () => navigate("/einstellungen") : undefined}
              >
                {user.kuerzel}
                {user.rolle === 'admin' && <span className="navbar-admin-dot" title="Admin">●</span>}
              </span>
              {user.rolle === 'admin' && (
                <button
                  className="navbar-logout-btn"
                  onClick={() => navigate(WELLE38_UI ? "/verwaltung" : "/admin")}
                  title="Verwaltung"
                  aria-label="Admin"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </button>
              )}
              <button
                className="navbar-logout-btn"
                onClick={handleLogout}
                title="Abmelden"
                aria-label="Abmelden"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Welle 104.1: Gemini gesperrt → auf ALLEN Seiten sichtbar */}
      {isAuthenticated && <GeminiBalken />}

      {showTestModal && (
        <div style={overlayStyle} onClick={closeTestModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Automatischer Testlauf</h2>
            <p>Starte einen kompletten End‑to‑End‑Test: Vorlagen → Input kopieren → Batch laufen lassen → Ergebnisse vergleichen.</p>

            {errorMsg && (
              <div style={{ background:'#ffe6e6', color:'#a30000', padding:'8px', borderRadius:6, marginBottom:8 }}>
                {errorMsg}
              </div>
            )}

            <div style={{ display:'grid', gap: 8 }}>
              {progress && (
                <div style={{ border:'1px solid #eee', borderRadius:6, padding:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <strong>Status</strong>
                    {testRunning ? badge('läuft', '#6a5acd') : badge('fertig', '#2e7d32')}
                  </div>
                  <pre style={{ whiteSpace:'pre-wrap', margin:0 }}>
                    {(() => {
                      const p = progress;
                      const lines = [];
                      if (p && typeof p === 'object') {
                        if (p.message) lines.push(`Message: ${p.message}`);
                        if (p.total != null) lines.push(`Gesamt: ${p.total}`);
                        if (p.processed != null) lines.push(`Verarbeitet: ${p.processed}`);
                        if (p.ok_count != null) lines.push(`OK: ${p.ok_count}`);
                        if (p.fail_count != null) lines.push(`Fehler: ${p.fail_count}`);
                        if (p.running != null) lines.push(`Running: ${p.running}`);
                        if (p.phase) lines.push(`Phase: ${p.phase}`);
                      }
                      return lines.length ? lines.join('\n') : JSON.stringify(p, null, 2);
                    })()}
                  </pre>
                </div>
              )}

              {lastResult && (
                <div style={{ border:'1px solid #eee', borderRadius:6, padding:10 }}>
                  <strong>Letztes Ergebnis</strong>
                  <pre style={{ whiteSpace:'pre-wrap', margin:0 }}>{JSON.stringify(lastResult, null, 2)}</pre>
                </div>
              )}
            </div>

            <div style={footerStyle}>
              <button onClick={closeTestModal}>Schließen</button>
              <button onClick={startTest} disabled={testRunning}>
                {testRunning ? 'Läuft…' : 'Testlauf starten'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;